import React, { memo, useState, useRef, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
	PayPalHostedFieldsProvider,
	PayPalHostedField,
	PAYPAL_HOSTED_FIELDS_TYPES,
	usePayPalHostedFields,
	PayPalButtons,
	FUNDING,
} from '@paypal/react-paypal-js';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { ApiError, isErrorRes } from 'requests/RequestBase';
import { CircularProgress, Backdrop } from '@material-ui/core';
import { Hr } from 'components/atoms/Hr';
import {
	PaymentToken,
	callGenerateBillingAgreementIdApi,
	callGenerateBillingAgreementTokenApi,
} from 'requests/RequestPayment';
import {
	GetSavedCardResData,
	callGetSavedCardApi,
	callDeleteSavedCardApi,
	callRegisterCardApi,
	RegisterCardResData,
	callChangeCardApi,
} from 'requests/RequestCard';
import { RoutePath } from 'consts/RoutePath';
import classes from './override.module.scss';
import { displayCardBrandImage } from 'components/molecules/card-brand/CardBrand';
import { monthSelectList, CARD_MAX_SIZE, CARD_MAX_MESSAGE } from 'consts/CardConst';
import { ApiErrorCode, findErrorMessageByApiError } from 'consts/ApiErrorCode';
import { useAgreeSendingCardInfo } from 'hooks/UseAgreeSendingCardInfo';
import { AgreeSendingCardInfo } from 'components/molecules/agree-sending-card-info/AgreeSendingCardInfo';
import { PaymentType, cardManagementPage, findPaymentByKey, isCredit, isPayPal } from 'consts/PaymentConst';
import { PaymentButtonBox } from 'components/molecules/payment-button-box/PaymentButtonBox';
import { NewCardRegistrationForm } from 'components/organisms/new-card-registration-form/NewCardRegistrationForm';
import { SubscriptionListTable } from 'components/organisms/subscription-list-table/SubscriptionListTable';
import { SelectingCreditCardInfo } from 'components/molecules/selecting-credit-card-info/SelectingCreditCardInfo';
import { PaymentTypeCheckForm } from 'components/molecules/payment-type-check-form/PaymentTypeCheckForm';
import { NewCardCheckForm } from 'components/molecules/new-card-check-form/NewCardCheckForm';
import { SelectOtherCards } from 'components/molecules/select-other-cards/SelectOtherCards';
import { PrimaryButton } from 'components/atoms/primary-button/PrimaryButton';
import { SubscriptionInfo } from 'components/organisms/subscription-info/SubscriptionInfo';
import {
	PeriodicPaymentDetailToken,
	PeriodicPaymentToken,
	callChangePeriodicPaymentApi,
	callGetPeriodicPaymentApi,
} from 'requests/RequestPeriodicPayment';

type Props = {};

type SubmitParams = {
	label?: string;
};

export const CardManagementTemplate = memo((_props: Props) => {
	const history = useHistory();
	// 3Dセキュア同意ボタン
	const { isAgreeSendCardInfo, setIsAgreeSendCardInfo, handleChangeAgreeCheckValue } = useAgreeSendingCardInfo();

	// ------定数 state------
	// 新規カード選択値
	const NEW_CARD_SELECT_VALUE = 9999;
	// API実行時画面管理
	const [processLoading, setProcessLoading] = useState(false);
	const [subscriptionLoading, setSubscriptionLoading] = useState(false);

	const [error, setError] = useState('');
	// ページルーティング
	const pageList: cardManagementPage = {
		DEFAULT_PAGE: 0,
		CARD_INPUT_PAGE: 1,
		CARD_DELETE_PAGE: 2,
		CARD_DELETE_COMPLETE: 3,
		CARD_INFO_PAGE: 4,
		CARD_REGISTER_COMPLETE: 5,
		CARD_UPDATE_COMPLETE: 6,
		SUBSCRIPTION_INFO_PAGE: 7,
		SUBSCRIPTION_CHANGE_PAGE: 8,
		SUBSCRIPTION_SHOW_SAVED_CARD: 9,
		SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE: 10,
		UN_SUPPORTED: 98,
		SERVICE_ERROR: 99,
	};
	// 表示ページ
	const [page, setPage] = useState(pageList.DEFAULT_PAGE);
	// スクロール位置情報
	const [scrollPosition, setScrollPosition] = useState<number>(undefined);

	// 定期支払方法変更可能フラグ
	const [modifiablePeriodicPaymentFlg, setModifiablePeriodicPaymentFlg] = useState<boolean>(false);
	// 定期購読一覧
	const [periodicPaymentList, setPeriodicPaymentList] = useState<PeriodicPaymentToken[]>([]);
	// 定期購読詳細情報
	const [periodicPaymentDetail, setPeriodicPaymentDetail] = useState<PeriodicPaymentDetailToken>(undefined);
	// 保存済みカード情報
	const [paymentTokens, setPaymentTokens] = useState<PaymentToken[]>([]);
	// 保存済みカード情報最大数(最大5枚)
	const isPaymentTokenMax = paymentTokens?.length === CARD_MAX_SIZE;
	// 保存済みカード情報の有無
	const [isPaymentTokenExists, setIsPaymentTokenExists] = useState<boolean>(paymentTokens?.length > 0);
	// 選択カード
	const [selectedCardIndex, setSelectedCardIndex] = useState<number>(undefined);
	// 選択済み定期購読情報
	const [selectedSubscriptionIndex, setSelectedSubscriptionIndex] = useState<number>(undefined);
	// 選択済み定期購読支払方法
	const [selectedSubscriptionPaymentKey, setSelectedSubscriptionPaymentKey] = useState<PaymentType>(undefined);
	// 前回選択した定期購読情報との比較
	const [comparisonPrevSelectedSubscriptionFlg, setComparisonPrevSelectedSubscriptionFlg] = useState<boolean>(false);
	// カード削除処理
	const [deleteCardLoading, setDeleteCardLoading] = useState(false);

	// API用の状態管理
	const [submitLoading, setSubmitLoading] = useState(false);
	const [isSubmitOk, setIsSubmitOk] = useState(false);
	const [orderId, setOrderId] = useState('');

	// カード情報保存ボタン押下済みか
	const [isSaveCard, setIsSaveCard] = useState(false);

	// 定期購読詳細情報更新フラグ
	const [paymentUpdateFlg, setPaymentUpdateFlg] = useState<boolean>(true);
	// 定期購読情報一覧更新フラグ
	const [paymentListUpdateFlg, setPaymentListUpdateFlg] = useState<boolean>(true);
	// クレジットカード情報更新フラグ
	const [cardInfoUpdateFlg, setCardInfoUpdateFlg] = useState<boolean>(true);

	// ------API------
	// 保存済みクレジットカード情報取得処理
	useEffect(() => {
		if (cardInfoUpdateFlg) {
			const execute = async () => {
				setProcessLoading(true);
				const result = await callGetSavedCardApi();
				if (result.status !== HttpStatus.OK) {
					setProcessLoading(false);
					history.push(RoutePath.ERROR);
					return;
				}
				const data = result.data as GetSavedCardResData;
				setPaymentTokens(data.payment_tokens);
				setIsPaymentTokenExists(data.payment_tokens?.length > 0);
				setCardInfoUpdateFlg(false);
				setProcessLoading(false);
			};
			execute();
		}
	}, [
		[pageList.DEFAULT_PAGE, pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE].includes(page) && cardInfoUpdateFlg === true,
	]);

	// 定期購読情報取得API
	useEffect(() => {
		if (paymentListUpdateFlg) {
			const execute = async () => {
				setSubscriptionLoading(true);
				const result = await callGetPeriodicPaymentApi();
				if (result.status !== HttpStatus.OK) {
					setSubscriptionLoading(false);
					history.push(RoutePath.ERROR);
					return;
				}

				const periodicPayments: PeriodicPaymentToken[] = [];
				result.data.periodic_payments.map((paymentToken) => {
					const newPaymentToken = {
						...paymentToken,
						first_capture_date: new Date(paymentToken.first_capture_date),
						last_capture_date: new Date(paymentToken.last_capture_date),
					};
					periodicPayments.push(newPaymentToken);
					return periodicPayments;
				});
				setSubscriptionLoading(false);
				setPaymentListUpdateFlg(false);
				setPeriodicPaymentList(periodicPayments);
			};
			execute();
		}
	}, [page === pageList.DEFAULT_PAGE && paymentListUpdateFlg === true]);

	// カード削除処理
	const deleteCard = async () => {
		setDeleteCardLoading(true);
		const result = await callDeleteSavedCardApi(paymentTokens[selectedCardIndex].payment_id);
		if (result.status === HttpStatus.GATEWAY_TIMEOUT) {
			setError(ReactError.E0009);
			setDeleteCardLoading(false);
			return;
		}
		if (result.status !== HttpStatus.OK) {
			const errorData = result.data as ApiError;
			setError(errorData.message);
			setDeleteCardLoading(false);
			return;
		}
		setDeleteCardLoading(false);
		paymentTokens.splice(selectedCardIndex, 1);
		setPage(pageList.CARD_DELETE_COMPLETE);
	};

	// クレジットカード支払いボタン
	const SubmitPayment = (props: SubmitParams) => {
		// HostedFieldsHandler型だとonメソッドが実装されていないためあえてany型で受け取る
		const hostedFields: any = usePayPalHostedFields();
		// クレジットカードのバリデーション(正しい値が入力されるまで支払ボタンをdisabled)
		const [isFormValid, setIsFormValid] = useState(false);
		// 入力済み&ボタン再レンダリング時の初回判定
		useEffect(() => {
			if (!hostedFields) {
				return;
			}
			const state = hostedFields.getState();
			const isValid = Object.keys(state.fields).every((key) => state.fields[key].isValid);
			setIsFormValid(isValid);
		}, []);
		// 入力時都度判定
		hostedFields?.on('validityChange', () => {
			const state = hostedFields.getState();
			const isValid = Object.keys(state.fields).every((key) => state.fields[key].isValid);
			setIsFormValid(isValid);
		});

		// クレジットカードで支払うボタン押下時処理
		const submitHandler = async () => {
			setSubmitLoading(true);
			try {
				await hostedFields
					.submit({
						vault: true,
						contingencies: ['SCA_ALWAYS'],
					})
					.then(function (payload) {
						// 3Dセキュア認証キャンセルの場合、支払い不能カードとして処理する
						if (payload.authenticationReason === 'SKIPPED_BY_BUYER' || payload.authenticationReason === 'FAILURE') {
							setError(ReactError.E0014);
							scrollTop();
							throw Error(`3Dsecure Failed`);
						}
					});
			} catch (error) {
				console.error(`error: ${JSON.stringify(error)}`);
				initializeSubmitState();
				scrollTop();
				setError(ReactError.E0015);
				return;
			} finally {
				setIsSubmitOk(true);
			}
		};

		return (
			<div className="btn gr">
				<button
					onClick={submitHandler}
					className={!isFormValid || !isAgreeSendCardInfo ? classes.disabled : ''}
					disabled={!isFormValid || !isAgreeSendCardInfo}
				>
					{props.label ? props.label : selectedCardIndex == null ? `登録` : `変更`}
				</button>
			</div>
		);
	};

	// クレジットカード登録・変更ボタン押下後処理(新規作成時のみ実行)
	const createOrder = async () => {
		const result = await callRegisterCardApi();
		if ((result.status !== HttpStatus.OK && isErrorRes(result.data)) || !isRegisterCardRes(result.data)) {
			history.push(RoutePath.ERROR);
			return;
		}
		setOrderId(result.data.paypal_order_id);
		return result.data.paypal_order_id;
	};
	const isRegisterCardRes = (obj: any): obj is RegisterCardResData => {
		return 'paypal_order_id' in obj;
	};

	// createOrder後に発火
	// SubmitPayment.submitHandler内ではタイミング的にorderIdが取得できないため仕方なくuseEffectにしている
	useEffect(() => {
		if (!orderId || !isSubmitOk) {
			return;
		}
		// 定期購読支払方法API、新規カードへ変更時処理
		if (modifiablePeriodicPaymentFlg) {
			const execute = async () => {
				setSubmitLoading(true);
				const changePeriodicPaymentResult = await callChangePeriodicPaymentApi({
					paypal_order_id: orderId,
					periodic_payment_id: periodicPaymentDetail.periodic_payment_id,
				});
				if (changePeriodicPaymentResult.status !== HttpStatus.OK && isErrorRes(changePeriodicPaymentResult.data)) {
					const error = changePeriodicPaymentResult.data;
					console.error(`error: ${JSON.stringify(error)}`);
					if (error.error_code === ApiErrorCode.E1180.code) {
						initializeSubmitState();
						setError(findErrorMessageByApiError(error));
						return;
					}
					setSubmitLoading(false);
					history.push(RoutePath.ERROR);
				}
				initializeSubmitState();
				setCardInfoUpdateFlg(true);
				setPaymentListUpdateFlg(true);
				setPaymentUpdateFlg(true);
				setPage(pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE);
			};
			execute();
		} else {
			// 登録カード情報変更API
			const execute = async () => {
				const changeCardResult = await callChangeCardApi({
					paypal_order_id: orderId,
					payment_tokens_id: paymentTokens[selectedCardIndex]?.payment_id,
				});
				if (changeCardResult.status !== HttpStatus.OK && isErrorRes(changeCardResult.data)) {
					const error = changeCardResult.data;
					console.error(`error: ${JSON.stringify(error)}`);
					if (error.error_code === ApiErrorCode.E1180.code) {
						initializeSubmitState();
						setError(findErrorMessageByApiError(error));
						return;
					}
					setSubmitLoading(false);
					history.push(RoutePath.ERROR);
				}
				initializeSubmitState();
				setPage(selectedCardIndex == null ? pageList.CARD_REGISTER_COMPLETE : pageList.CARD_UPDATE_COMPLETE);
				setCardInfoUpdateFlg(true);
				setPaymentUpdateFlg(true);
			};
			execute();
		}
	}, [orderId, isSubmitOk]);

	// 定期購読支払方法API、登録済みカードへ変更処理
	const paySavedCreditCard = async () => {
		setSubmitLoading(true);
		// 定期購読支払方法変更処理　クレジットカード対応
		const changePeriodicPaymentResult = await callChangePeriodicPaymentApi({
			payment_tokens_id: paymentTokens[selectedCardIndex]?.payment_id,
			periodic_payment_id: periodicPaymentDetail.periodic_payment_id,
		});
		if (changePeriodicPaymentResult.status !== HttpStatus.OK && isErrorRes(changePeriodicPaymentResult.data)) {
			const error = changePeriodicPaymentResult.data;
			console.error(`error: ${JSON.stringify(error)}`);
			if (error.error_code === ApiErrorCode.E1180.code) {
				initializeSubmitState();
				setError(findErrorMessageByApiError(error));
				return;
			}
			setSubmitLoading(false);
			history.push(RoutePath.ERROR);
		}
		initializeSubmitState();
		setPaymentListUpdateFlg(true);
		setPaymentUpdateFlg(true);
		setPage(pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE);
	};

	// PayPalボタン用 定期購読トークン作成(※1ユーザー1回のみ)
	const createBillingAgreementToken = async () => {
		const createBillingAgreementTokenResult = await callGenerateBillingAgreementTokenApi();
		if (createBillingAgreementTokenResult.status !== HttpStatus.OK) {
			history.push(RoutePath.ERROR);
			setSubmitLoading(false);
			return;
		}
		return createBillingAgreementTokenResult.data.token_id;
	};

	// PayPalボタン用 定期購読ID作成(※1ユーザー1回のみ)
	const onApprove = async (data) => {
		setSubmitLoading(true);
		const createBillingAgreementIdResult = await callGenerateBillingAgreementIdApi({
			token_id: data.billingToken,
		});
		if (createBillingAgreementIdResult.status !== HttpStatus.OK) {
			history.push(RoutePath.ERROR);
			return;
		}
		// 定期購読支払方法変更処理　PayPal
		const changePeriodicPaymentResult = await callChangePeriodicPaymentApi({
			agreement_id: createBillingAgreementIdResult.data.agreement_id,
			periodic_payment_id: periodicPaymentDetail.periodic_payment_id,
		});
		if (changePeriodicPaymentResult.status !== HttpStatus.OK && isErrorRes(changePeriodicPaymentResult.data)) {
			const error = changePeriodicPaymentResult.data;
			console.error(`error: ${JSON.stringify(error)}`);
			if (error.error_code === ApiErrorCode.E1180.code) {
				initializeSubmitState();
				setError(findErrorMessageByApiError(error));
				return;
			}
			setSubmitLoading(false);
			history.push(RoutePath.ERROR);
			return;
		}
		initializeSubmitState();
		setSelectedCardIndex(undefined);
		setPaymentListUpdateFlg(true);
		setPaymentUpdateFlg(true);
		setPage(pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE);
	};

	// ------関数 function------
	const scrollRef = useRef(null);
	const scrollTop = () => {
		scrollRef.current.scrollIntoView();
	};

	useEffect(() => {
		if (page !== pageList.DEFAULT_PAGE) {
			scrollTop();
			document.title = 'GakkenIDマイページ｜お支払い情報';
		} else {
			if (selectedSubscriptionIndex != null) {
				setSelectedSubscriptionPaymentKey(undefined);
				// スクロール位置を設定する
				window.scroll(0, scrollPosition);
				setScrollPosition(undefined);
			}
		}
		if ([pageList.DEFAULT_PAGE, pageList.SUBSCRIPTION_INFO_PAGE].includes(page)) {
			setIsAgreeSendCardInfo(false);
			setError('');
		}
	}, [page]);

	// 基本画面に遷移
	const changeDefaultPage = () => {
		setPage(pageList.DEFAULT_PAGE);
		setSelectedCardIndex(undefined);
		setModifiablePeriodicPaymentFlg(false);
	};

	// カード情報画面へ戻る処理
	const backInInputCardPage = () => {
		setPage(selectedCardIndex === undefined ? pageList.DEFAULT_PAGE : pageList.CARD_INFO_PAGE);
	};

	// 選択カード変更時処理
	const handleChangeSavedCardSelectValue = async (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedCardIndex(Number(event.target.value));
	};

	// カード選択済みか
	const isCardSelected = (i: number) => {
		return i === selectedCardIndex;
	};

	// カード選択処理
	const selectCard = (index: number) => {
		setSelectedCardIndex(index);
		setPage(pageList.CARD_INFO_PAGE);
		setScrollPosition(window.scrollY);
	};

	// API実行後API用のstate初期化
	const initializeSubmitState = () => {
		setOrderId('');
		setIsSubmitOk(false);
		setSubmitLoading(false);
	};

	// 定期購読一覧から選択処理
	const selectsubscription = (index: number) => {
		// 定期購読支払情報変更APIを実行許可
		setModifiablePeriodicPaymentFlg(true);

		setComparisonPrevSelectedSubscriptionFlg(selectedSubscriptionIndex !== index);
		setSelectedSubscriptionIndex(index);
		const payment = findPaymentByKey(Number(periodicPaymentList[index].payment_type));
		setSelectedSubscriptionPaymentKey(payment);

		setScrollPosition(window.scrollY);
		setPage(pageList.SUBSCRIPTION_INFO_PAGE);
	};

	// 定期購読、支払方法選択時処理
	const handleChangeRadioValue = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const payment = findPaymentByKey(Number(event.target.value));
		setSelectedSubscriptionPaymentKey(payment);
	};

	// クレジットカードチェックフォーム(保存済みチェック)表示時処理
	const handleChangeCheckValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsSaveCard(event.target.checked);
	};

	// 選択した定期購読支払情報がカード支払いの場合、カード情報を格納する
	const setCardIndex = (cardList: PaymentToken[], paymentDetail: PeriodicPaymentDetailToken) => {
		cardList.map((t, i) => t.last_digits === paymentDetail.last_digits && setSelectedCardIndex(i));
	};

	// 定期購読情報の中にカード情報が格納されているか
	const paymentValueEmptyCheck = (token: PeriodicPaymentDetailToken) => {
		const tokenKeys: string[] = ['brand', 'expiry', 'last_digits'];

		const periodicPaymentCardInfo: string[] = [];
		tokenKeys.map((key) => {
			if (token[key]) {
				periodicPaymentCardInfo.push(token[key]);
			}
			return periodicPaymentCardInfo;
		});
		const emptyCount: number = periodicPaymentCardInfo.filter((item) => item !== '').length;

		return emptyCount === tokenKeys.length;
	};

	return (
		<main>
			<Backdrop
				className={classes.backdrop}
				open={processLoading || deleteCardLoading || submitLoading || subscriptionLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<section className="section mypage" ref={scrollRef}>
				{page === pageList.DEFAULT_PAGE && (
					<>
						<div className="breadcrumb">
							<ul>
								<li>
									<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
								</li>
								<li>お支払い情報</li>
							</ul>
						</div>
						<h1 className="page-head">お支払い情報</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span className={classes.sectionHeadH1}>新規　クレジットカード追加</span>
							</div>
							<div className="section-txt-box">
								<div className="txt">
									<div>新規でクレジットカードの情報を追加したい場合は「追加」ボタンを押してください。</div>
									<div>{`（※追加できるカードの枚数は${CARD_MAX_SIZE}枚までとなりますのでご注意ください）`}</div>
								</div>
							</div>
							{!isPaymentTokenMax && (
								<div className="btn-box">
									<div className="btn gr">
										<button
											onClick={() => {
												setPage(pageList.CARD_INPUT_PAGE);
											}}
										>
											追加
										</button>
									</div>
								</div>
							)}
						</div>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span className={classes.sectionHeadH1}>登録済み　クレジットカード情報</span>
							</div>
							{paymentTokens?.length ? (
								<div className="info-box form-box">
									<div className="form-input">
										<div className="info-box">
											<div className={classes.cardManagementRowHead}>
												<div className={'info-head ' + classes.cardManagementRowHeadCol} />
												<div className={'info-head ' + classes.cardManagementRowHeadCol}>カード番号</div>
												<div className={'info-head ' + classes.cardManagementRowHeadCol}>有効期限</div>
												<div className={'info-head ' + classes.cardManagementRowHeadCol} />
											</div>
											{paymentTokens.map((t, i) => {
												return (
													<>
														<Hr />
														<div className={classes.cardManagementRow}>
															<div className="radio-box">
																<ul>
																	<li className={classes.cardManagementLi}>
																		<div className={`${classes.cardManagementCol}`}>
																			{displayCardBrandImage(t.brand)}
																		</div>
																		<div className={classes.cardManagementCol}>{`**** **** **** ${t.last_digits}`}</div>
																		<div className={classes.cardManagementCol}>{`${t.expiry.split('-')[1]} / ${
																			t.expiry.split('-')[0]
																		}`}</div>
																		<div
																			className={`${classes.cardManagementCol} ${classes.showInfoLink}`}
																			onClick={() => selectCard(i)}
																		>
																			<span className={classes.linkToMemberInfo}>&gt;</span>
																		</div>
																	</li>
																</ul>
															</div>
														</div>
													</>
												);
											})}
										</div>
									</div>
								</div>
							) : (
								<>
									<div className="section-head">
										<span className={classes.sectionHeadH2}>登録されているカードはありません。</span>
									</div>
									<div className="btn-box">
										<div className="btn gr">
											<Link to={RoutePath.MY_PAGE}>
												<span>マイページTOPへ戻る</span>
											</Link>
										</div>
									</div>
								</>
							)}
						</div>
						<SubscriptionListTable periodicPaymentList={periodicPaymentList} onClickEvent={selectsubscription} />
					</>
				)}
				{page === pageList.CARD_INFO_PAGE && (
					<>
						{/* カード情報表示の場合 */}
						<div className="breadcrumb">
							<ul>
								<li>
									<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
								</li>
								<li>
									<button className={classes.linkText} type="button" onClick={changeDefaultPage}>
										お支払い情報
									</button>
								</li>
								<li>クレジットカード情報</li>
							</ul>
						</div>
						<h1 className="page-head">クレジットカード情報</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span>カードの情報</span>
							</div>
							<div className={classes.paymentCreditCardInput}>
								<div className="form-box">
									<div className="form-head">
										<label>カード番号 （Card Number）</label>
									</div>
									<div
										className={`${classes.deleteCardNumber} ${classes.marginLeft20} ${classes.flexCenter} form-input width50`}
									>
										{displayCardBrandImage(paymentTokens[selectedCardIndex]?.brand)}
										<label>{`**** **** **** ${paymentTokens[selectedCardIndex]?.last_digits}`}</label>
									</div>
								</div>
								<div className="form-box">
									<div className="form-head">
										<label>有効期限 （Expiration Date）</label>
									</div>
									<div
										className={`${classes.marginLeft20} ${classes.paymentCreditExpirationDateRow} form-input width50`}
									>
										<label>{`${paymentTokens[selectedCardIndex]?.expiry.split('-')[1]} / ${
											paymentTokens[selectedCardIndex]?.expiry.split('-')[0]
										}`}</label>
									</div>
								</div>
							</div>
							<Hr />
							<div className={classes.sectionPaymentInfoRow}>
								<div className="btn-box">
									<div className="btn wh">
										<button onClick={() => setPage(pageList.CARD_DELETE_PAGE)}>削除</button>
									</div>
									<div className="btn gr">
										<button onClick={() => setPage(pageList.CARD_INPUT_PAGE)}>変更</button>
									</div>
								</div>
							</div>
						</div>
						<div className="btn-box">
							<div className="btn wh">
								<button
									onClick={() => {
										changeDefaultPage();
										setError('');
									}}
								>
									戻る
								</button>
							</div>
						</div>
					</>
				)}
				{page === pageList.CARD_INPUT_PAGE && (
					<>
						<div className="breadcrumb">
							<ul>
								<li>
									<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
								</li>
								<li>
									<button className={classes.linkText} type="button" onClick={changeDefaultPage}>
										お支払い情報
									</button>
								</li>
								<li>{selectedCardIndex == null ? `新規クレジットカードの追加` : `クレジットカード情報の変更`}</li>
							</ul>
						</div>
						<h1 className="page-head">
							{selectedCardIndex == null ? `新規クレジットカードの追加` : `クレジットカード情報の変更`}
						</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span>カード情報の入力</span>
							</div>
							{error !== '' && (
								<div className="error-box">
									<div className="error-box-in">
										<p>{error}</p>
									</div>
								</div>
							)}
							<PayPalHostedFieldsProvider
								// 次へ押下時にsubmitの前に発火
								createOrder={createOrder}
								// 資格情報が無い場合に発火
								notEligibleError={<div>Your account is not eligible</div>}
							>
								<PayPalHostedField
									hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.NUMBER}
									options={{ selector: '#card-number', placeholder: '********' }}
								/>
								<PayPalHostedField
									hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_MONTH}
									options={{
										selector: '#expiration-month',
										placeholder: 'MM',
										select: monthSelectList,
									}}
								/>
								<PayPalHostedField
									hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_YEAR}
									options={{
										selector: '#expiration-year',
										placeholder: 'YYYY',
										select: true,
									}}
								/>
								<PayPalHostedField
									hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.CVV}
									options={{ selector: '#cvv', placeholder: '123' }}
								/>
								<div className={classes.paymentCreditCardInput}>
									<div className="form-box">
										<div className="form-head">
											<label>ご利用いただけるクレジットカード</label>
										</div>
										<div className={`${classes.flexCenter} form-input width50`}>{displayCardBrandImage()}</div>
									</div>
									<div className="form-box">
										<div className="form-head">
											<label>カード番号 （Card Number）</label>
											<span className="required">必須</span>
										</div>
										<div className="form-input width50">
											<div id="card-number" className={classes.cardField} />
										</div>
									</div>
									<div className="form-box">
										<div className="form-head">
											<label>有効期限 （Expiration Date）</label>
											<span className="required">必須</span>
										</div>
										<div className={classes.paymentCreditExpirationDateRow + ` form-input width50`}>
											<div className={classes.paymentCreditExpirationDateSelectBox}>
												<div id="expiration-month" className={classes.cardField} />
											</div>
											<span className={classes.paymentCreditExpirationDateSlash}>/</span>
											<div className={classes.paymentCreditExpirationDateSelectBox}>
												<div id="expiration-year" className={classes.cardField} />
											</div>
										</div>
									</div>
									<div className="form-box">
										<div className="form-head">
											<label>セキュリティコード （Security Codes）</label>
											<span className="required">必須</span>
										</div>
										<div className="form-input width50">
											<div id="cvv" className={classes.cardField} />
										</div>
									</div>
									<AgreeSendingCardInfo
										isAgreeSendCardInfo={isAgreeSendCardInfo}
										handleChangeAgreeCheckValue={handleChangeAgreeCheckValue}
									/>
								</div>
								<div className="btn-box">
									<div className="btn wh">
										<button onClick={backInInputCardPage}>戻る</button>
									</div>
									<SubmitPayment />
								</div>
							</PayPalHostedFieldsProvider>
						</div>
					</>
				)}
				{page === pageList.CARD_DELETE_PAGE && (
					<>
						{/* カード削除の場合 */}
						<div className="breadcrumb">
							<ul>
								<li>
									<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
								</li>
								<li>
									<button className={classes.linkText} type="button" onClick={changeDefaultPage}>
										お支払い情報
									</button>
								</li>
								<li>クレジットカード情報</li>
							</ul>
						</div>
						<h1 className="page-head">クレジットカード情報</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span>カード情報の削除</span>
							</div>
							<div className="thankyou-box">
								<img className={classes.thankyouDeleteImage} src="images/delete.svg" alt="削除してよろしいでしょうか" />
							</div>
							<p>
								<div>保存済みのクレジットカード情報を削除します</div>
							</p>
							<Hr />
							{error !== '' && (
								<div className="error-box">
									<div className="error-box-in">
										<p>{error}</p>
									</div>
								</div>
							)}
							<div className={classes.paymentCreditCardInput}>
								<div className="form-box">
									<div className="form-head">
										<label>カード番号 （Card Number）</label>
									</div>
									<div
										className={`${classes.deleteCardNumber} ${classes.marginLeft20} ${classes.flexCenter} form-input width50`}
									>
										{displayCardBrandImage(paymentTokens[selectedCardIndex]?.brand)}
										<label>{`**** **** **** ${paymentTokens[selectedCardIndex]?.last_digits}`}</label>
									</div>
								</div>
								<div className="form-box">
									<div className="form-head">
										<label>有効期限 （Expiration Date）</label>
									</div>
									<div
										className={`${classes.marginLeft20} ${classes.paymentCreditExpirationDateRow} form-input width50`}
									>
										<label>{`${paymentTokens[selectedCardIndex]?.expiry.split('-')[1]} / ${
											paymentTokens[selectedCardIndex]?.expiry.split('-')[0]
										}`}</label>
									</div>
								</div>
							</div>
							<Hr />
							<div className={classes.sectionPaymentInfoRow}>
								<div className="btn-box">
									<div className="btn wh">
										<button
											onClick={() => {
												setPage(pageList.CARD_INFO_PAGE);
												setError('');
											}}
										>
											戻る
										</button>
									</div>
									<div className="btn gr">
										<button onClick={deleteCard}>削除</button>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
				{page === pageList.CARD_DELETE_COMPLETE && (
					<>
						<div className="breadcrumb">
							<ul>
								<li>
									<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
								</li>
								<li>
									<button className={classes.linkText} type="button" onClick={changeDefaultPage}>
										お支払い情報
									</button>
								</li>
								<li>クレジットカード情報</li>
							</ul>
						</div>
						<h1 className="page-head">クレジットカード情報</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span>カード情報の削除</span>
							</div>
							<div className="thankyou-box">
								<img src="images/img-complete.svg" alt="完了しました" />
							</div>
							<div className="btn-box">
								<div className="btn gr">
									<button
										onClick={() => {
											history.push(RoutePath.MY_PAGE);
										}}
									>
										マイページTOPへ戻る
									</button>
								</div>
							</div>
						</div>
					</>
				)}
				{[
					pageList.CARD_REGISTER_COMPLETE,
					pageList.CARD_UPDATE_COMPLETE,
					pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE,
				].includes(page) && (
					<>
						<div className="breadcrumb">
							<ul>
								<li>
									<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
								</li>
								<li>
									<button className={classes.linkText} type="button" onClick={changeDefaultPage}>
										お支払い情報
									</button>
								</li>
								{pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE === page ? (
									<>
										<li>
											<button
												className={classes.linkText}
												type="button"
												onClick={() => setPage(pageList.SUBSCRIPTION_INFO_PAGE)}
											>
												定期購読詳細
											</button>
										</li>
										<li>お支払い方法の変更</li>
									</>
								) : (
									<li>{selectedCardIndex === null ? `新規クレジットカードの追加` : `クレジットカード情報の変更`}</li>
								)}
							</ul>
						</div>
						<h1 className="page-head">
							{pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE === page
								? 'お支払い方法の変更'
								: pageList.CARD_REGISTER_COMPLETE === page
								? `新規クレジットカードの追加`
								: `クレジットカード情報の変更`}
						</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span>
									{pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE === page
										? 'お支払い方法の更新'
										: pageList.CARD_REGISTER_COMPLETE === page
										? `カード情報の登録`
										: `カード情報の更新`}
								</span>
							</div>
							<div className="thankyou-box">
								<img src="images/img-complete.svg" alt="完了しました" />
							</div>
							<div className="btn-box">
								<div className="btn gr">
									<button
										onClick={() => {
											pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE === page
												? setPage(pageList.SUBSCRIPTION_INFO_PAGE)
												: history.push(RoutePath.MY_PAGE);
										}}
									>
										{pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE === page
											? '定期購読詳細画面へ戻る'
											: 'マイページTOPへ戻る'}
									</button>
								</div>
							</div>
						</div>
					</>
				)}
				{/* 定期購読詳細ページ */}
				{page === pageList.SUBSCRIPTION_INFO_PAGE && (
					<>
						{/* 定期購読詳細の場合 */}
						<div className="breadcrumb">
							<ul>
								<li>
									<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
								</li>
								<li>
									<button className={classes.linkText} type="button" onClick={changeDefaultPage}>
										お支払い情報
									</button>
								</li>
								<li>定期購読詳細</li>
							</ul>
						</div>
						<SubscriptionInfo
							paymentTokens={paymentTokens}
							periodicPaymentId={periodicPaymentList[selectedSubscriptionIndex].periodic_payment_id}
							periodicPaymentDetail={periodicPaymentDetail}
							setPeriodicPaymentDetail={setPeriodicPaymentDetail}
							setLoadingState={setSubscriptionLoading}
							paymentUpdateFlg={paymentUpdateFlg}
							setPaymentUpdateFlg={setPaymentUpdateFlg}
							comparisonFlg={comparisonPrevSelectedSubscriptionFlg}
							setComparisonFlg={setComparisonPrevSelectedSubscriptionFlg}
							setCardIndex={setCardIndex}
							activeEvent={() => {
								setPage(pageList.SUBSCRIPTION_CHANGE_PAGE);
								setError('');
							}}
							passiveEvent={changeDefaultPage}
						/>
					</>
				)}
				{/* 定期購読支払方法変更画面 */}
				{page === pageList.SUBSCRIPTION_CHANGE_PAGE && (
					<>
						<div className="breadcrumb">
							<ul>
								<li>
									<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
								</li>
								<li>
									<button className={classes.linkText} type="button" onClick={changeDefaultPage}>
										お支払い情報
									</button>
								</li>
								<li>
									<button
										className={classes.linkText}
										type="button"
										onClick={() => setPage(pageList.SUBSCRIPTION_INFO_PAGE)}
									>
										定期購読詳細
									</button>
								</li>
								<li>お支払い方法の変更</li>
							</ul>
						</div>
						<h1 className="page-head">お支払い方法の変更</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="info-box form-box">
								<div className="form-input">
									<div className="section-head">
										<span>お支払方法の選択</span>
									</div>
									<div className="section-txt-box">
										<div className="txt">
											<div>すべての取引は安全で、暗号化されています。</div>
										</div>
									</div>
									{periodicPaymentDetail.payment_specify_list?.paypal_hidden_flg !== 1 &&
										periodicPaymentDetail.site_settlement_setting_display_flg.paypal_display_flg === 1 && (
											<>
												<PaymentTypeCheckForm
													selectedPaymentKey={selectedSubscriptionPaymentKey}
													onChangeEvent={handleChangeRadioValue}
													isPaymentType={isPayPal}
													paymentType={'paypal'}
													label={'PayPal決済'}
												/>
												{isPayPal(selectedSubscriptionPaymentKey) && (
													<PayPalButtons
														// PayPalボタンのみ表示
														fundingSource={FUNDING.PAYPAL}
														style={{ label: 'pay' }}
														// PayPalボタン押下時に発火
														createBillingAgreement={createBillingAgreementToken}
														// PayPalポップアップ閉じた後に発火
														onApprove={(data) => onApprove(data)}
														onError={(error) => {
															history.push(RoutePath.ERROR);
															console.error(`error: ${JSON.stringify(error)}`);
														}}
													/>
												)}
												<Hr />
											</>
										)}

									{periodicPaymentDetail.payment_specify_list?.credit_hidden_flg !== 1 &&
										periodicPaymentDetail.site_settlement_setting_display_flg.credit_card_display_flg === 1 && (
											<>
												<div className={classes.sectionPaymentInfoRow}>
													<PaymentTypeCheckForm
														selectedPaymentKey={selectedSubscriptionPaymentKey}
														onChangeEvent={handleChangeRadioValue}
														paymentTokens={periodicPaymentDetail}
														ispaymentValueEmpty={paymentValueEmptyCheck(periodicPaymentDetail)}
														isCardSelected={isCardSelected}
														isPaymentType={isCredit}
														paymentType={'credit'}
														label={'クレジットカード'}
														NEW_CARD_SELECT_VALUE={NEW_CARD_SELECT_VALUE}
													/>
													{/* 保存済みカード情報IDがないor新規カードが選択されている場合は入力フォーム */}
													{isCredit(selectedSubscriptionPaymentKey) &&
														periodicPaymentDetail.payment_type === '1' &&
														!paymentValueEmptyCheck(periodicPaymentDetail) &&
														!isPaymentTokenMax && (
															<NewCardRegistrationForm
																isSaveCard={isSaveCard}
																isAgreeSendCardInfo={isAgreeSendCardInfo}
																activeButtonMessage={'このカードに変更する'}
																passiveButtonVisibleFlg={isPaymentTokenExists}
																transitionPage={pageList.SUBSCRIPTION_SHOW_SAVED_CARD}
																backPage={pageList.SUBSCRIPTION_INFO_PAGE}
																setPage={setPage}
																createOrder={createOrder}
																handleChangeCheckValue={handleChangeCheckValue}
																handleChangeAgreeCheckValue={handleChangeAgreeCheckValue}
																SubmitPayment={SubmitPayment}
																selectedPeriodicPaymentFlg={modifiablePeriodicPaymentFlg}
															/>
														)}
													{/* 保存済みカード情報IDがある&新規カード以外が選択されている場合は閲覧のみ */}
													{isCredit(selectedSubscriptionPaymentKey) &&
														periodicPaymentDetail.payment_type === '2' &&
														paymentValueEmptyCheck(periodicPaymentDetail) && (
															<>
																<SelectingCreditCardInfo paymentTokens={periodicPaymentDetail} />
																<PaymentButtonBox
																	activeMessage={'このカードで支払う'}
																	passiveMessage={'別のカードで支払う'}
																	activeEvent={() => setPage(pageList.SUBSCRIPTION_PAYMENT_UPDATE_COMPLETE)}
																	passiveEvent={() => setPage(pageList.SUBSCRIPTION_SHOW_SAVED_CARD)}
																/>
																<Hr />
															</>
														)}
												</div>
											</>
										)}
									{isCredit(selectedSubscriptionPaymentKey) &&
									isPaymentTokenMax &&
									!paymentValueEmptyCheck(periodicPaymentDetail) ? (
										<PaymentButtonBox
											activeMessage={'別のカードで支払う'}
											passiveMessage={'戻る'}
											activeEvent={() => setPage(pageList.SUBSCRIPTION_SHOW_SAVED_CARD)}
											passiveEvent={() => setPage(pageList.SUBSCRIPTION_INFO_PAGE)}
										/>
									) : (
										<>
											{isPayPal(selectedSubscriptionPaymentKey) &&
												periodicPaymentDetail.payment_specify_list.credit_hidden_flg === 0 &&
												periodicPaymentDetail.site_settlement_setting_display_flg.credit_card_display_flg === 1 && (
													<Hr />
												)}
											<div className="btn-box">
												<div className="btn wh">
													<PrimaryButton message="戻る" onClickEvent={() => setPage(pageList.SUBSCRIPTION_INFO_PAGE)} />
												</div>
											</div>
										</>
									)}
								</div>
							</div>
						</div>
						{(periodicPaymentDetail.payment_specify_list.credit_hidden_flg === 1 ||
							periodicPaymentDetail.site_settlement_setting_display_flg.credit_card_display_flg === 0) &&
							(periodicPaymentDetail.payment_specify_list.paypal_hidden_flg === 1 ||
								periodicPaymentDetail.site_settlement_setting_display_flg.paypal_display_flg === 0) &&
							history.push(RoutePath.ERROR)}
					</>
				)}
				{/* 定期購読別のカード参照画面 */}
				{page === pageList.SUBSCRIPTION_SHOW_SAVED_CARD && (
					<>
						<div className="breadcrumb">
							<ul>
								<li>
									<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
								</li>
								<li>
									<button className={classes.linkText} type="button" onClick={changeDefaultPage}>
										お支払い情報
									</button>
								</li>
								<li>
									<button
										className={classes.linkText}
										type="button"
										onClick={() => setPage(pageList.SUBSCRIPTION_INFO_PAGE)}
									>
										定期購読詳細
									</button>
								</li>
								<li>お支払い方法の変更</li>
							</ul>
						</div>
						<h1 className="page-head">お支払い方法の変更</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="info-box form-box">
								<div className="form-input">
									{/* 別のカードで支払う場合 */}
									<div className={`section-head`}>
										<span>クレジットカード情報</span>
									</div>
									{isPaymentTokenMax && (
										<div className="section-txt-box">
											<div className="txt">
												<div>{CARD_MAX_MESSAGE}</div>
											</div>
										</div>
									)}
									{error !== '' && (
										<div className="error-box">
											<div className="error-box-in">
												<p>{error}</p>
											</div>
										</div>
									)}
									<div className="info-box form-box">
										<div className="form-input">
											<div className="info-box">
												{isPaymentTokenExists && (
													<SelectOtherCards
														paymentTokens={paymentTokens}
														isCardSelected={isCardSelected}
														onChangeEvent={handleChangeSavedCardSelectValue}
														selectDeleteCard={deleteCard}
													/>
												)}
												{/* カード保存上限に達していなかったら新規カード入力選択肢を表示する */}
												{!isPaymentTokenMax && (
													<>
														<Hr />
														<div className={classes.sectionPaymentInfoRow}>
															<NewCardCheckForm
																onChangeEvent={handleChangeSavedCardSelectValue}
																isCardSelected={isCardSelected}
																paymentType={'credit'}
																label={'新規クレジットカード'}
																NEW_CARD_SELECT_VALUE={NEW_CARD_SELECT_VALUE}
															/>
															<NewCardRegistrationForm
																isSaveCard={isSaveCard}
																isAgreeSendCardInfo={isAgreeSendCardInfo}
																activeButtonMessage={'このカードに変更する'}
																buttonBoxVisibleFlg={isCardSelected(NEW_CARD_SELECT_VALUE)}
																transitionPage={pageList.SUBSCRIPTION_INFO_PAGE}
																setPage={setPage}
																createOrder={createOrder}
																handleChangeCheckValue={handleChangeCheckValue}
																handleChangeAgreeCheckValue={handleChangeAgreeCheckValue}
																SubmitPayment={SubmitPayment}
																passiveButtonVisibleFlg={false}
																backPage={pageList.SUBSCRIPTION_CHANGE_PAGE}
																selectedPeriodicPaymentFlg={modifiablePeriodicPaymentFlg}
															/>
														</div>
													</>
												)}
											</div>
											{!isCardSelected(NEW_CARD_SELECT_VALUE) && (
												<>
													<Hr />
													<div className={classes.sectionPaymentInfoRow}>
														<PaymentButtonBox
															activeMessage={'このカードに変更する'}
															passiveMessage={'戻る'}
															activeEvent={paySavedCreditCard}
															passiveEvent={() => {
																setPage(pageList.SUBSCRIPTION_CHANGE_PAGE);
															}}
															activeButtonDisabled={selectedCardIndex == null}
														/>
													</div>
												</>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
			</section>
		</main>
	);
});
